import type { RefObject } from 'react';
import { styled } from 'styled-components';

import type {
  InputAffixVariant,
  InputStyleVariant,
} from '#/components/Input/Input.types';
import { LocationInput } from '#/components/LocationInput/LocationInput';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';

import { Field } from './Field/Field';
import type { FieldCustomErrorMatcher } from './Field/FieldCustomErrorMatcher';

const StyledLocationInput = styled(LocationInput)`
  height: 45px;
`;

type Props = {
  name: string;
  id?: string;
  label?: string;
  description?: string;
  disabled?: boolean;
  includeCity?: boolean;
  includeFull?: boolean;
  handlePlaceDetails?: (place: google.maps.GeocoderResult) => void;
  showClearButton?: boolean;
  showLocationIcon?: boolean;
  placeholder?: string;
  required?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  onChange?: (searchLocation: SearchLocation | undefined) => void;
  onClear?: () => void;
  qaId?: string;
  customErrorMatch?: FieldCustomErrorMatcher;
  customError?: string | null | undefined;
  success?: boolean;
  includedRegionCodes?: string[];
  noWrap?: boolean;
  noMargin?: boolean;
  addressTypes?: string[];
  locationText?: string | null | undefined;
  prefixVariant?: InputAffixVariant;
  styleVariant?: InputStyleVariant;
};

export function LocationField({
  name,
  id,
  label,
  description,
  disabled,
  includeCity,
  includeFull,
  handlePlaceDetails,
  showClearButton,
  showLocationIcon,
  placeholder,
  required,
  inputRef,
  onChange,
  onClear,
  customErrorMatch,
  customError,
  success,
  locationText,
  addressTypes,
  includedRegionCodes,
  noWrap,
  noMargin,
  styleVariant = 'default',
  prefixVariant = 'transparent',
  qaId,
}: Props) {
  return (
    <Field
      customErrorMatch={customErrorMatch}
      inputProps={{ disabled }}
      name={name}
      customError={customError}
      success={success}
      noMargin={noMargin}
      noWrap={noWrap}
      label={label}
      description={description}
      renderInput={({
        fieldQaIdPrefix,
        value,
        qaId: fieldQaId,
        hasSuccess,
        updateValue,
      }) => {
        if (value && typeof value === 'object') {
          if (value.text) {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line no-param-reassign
            locationText = value.text;
          }

          // @TODO: possible to use just `full` or `text` on the client?
          if (value.full && includeFull) {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line no-param-reassign
            locationText = value.full;
          }
        }

        const defaultId = [fieldQaIdPrefix, qaId].filter(Boolean).join('-');

        return (
          <StyledLocationInput
            locationText={locationText}
            id={id ?? defaultId}
            qaId={qaId ? defaultId : fieldQaId}
            onChange={(searchLocation) => {
              updateValue(searchLocation);

              if (onChange) {
                onChange(searchLocation);
              }
            }}
            handlePlaceDetails={handlePlaceDetails}
            hasSuccess={hasSuccess}
            onClear={onClear}
            disabled={disabled}
            showClearButton={showClearButton}
            showLocationIcon={showLocationIcon}
            includeCity={includeCity}
            includeFull={includeFull}
            placeholder={placeholder}
            required={required}
            inputRef={inputRef}
            addressTypes={addressTypes}
            includedRegionCodes={includedRegionCodes}
            styleVariant={styleVariant}
            affixVariant={prefixVariant}
          />
        );
      }}
    />
  );
}
